<template>
  <div id="watch-modal">
    <v-dialog :value="isModalActive" max-width="800" @input="inputModal">
      <v-card
        v-if="isModalActive"
        id="watch-modal-card"
        class="py-3"
        :color="$vuetify.theme.dark ? 'dark_backgroundDarkBlue' : undefined"
      >
        <v-card-text class="py-0 my-0">
          <ApiErrorAlert ref="apiErrorAlert" :error="error" class="mt-3" />

          <v-row>
            <template v-if="!editing">
              <v-col cols="12" md="12" class="my-0 py-0 pt-2">
                <h1 class="card-title">
                  {{ $t(`views.watchList.watchModal.watchForm.title`) }}
                </h1>

                <div class="watch-form-select-data-from-position">
                  <v-menu
                    v-model="getDataFromPositionActive"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="400px"
                    class="watch-form-select-data-from-position-menu"
                    content-class="elevation-1"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <a class="primary--text" v-bind="attrs" v-on="on">
                        {{ $t(`views.watchList.watchModal.watchForm.getDataFromPosition`) }}
                      </a>
                    </template>
                    <v-card
                      class="watch-form-select-data-from-position-menu-card pt-3 pb-4 px-4 my-0"
                    >
                      <PositionSearch
                        v-if="getDataFromPositionActive && isModalActive"
                        ref="positionSearch"
                        :disabled="loading"
                        :type="watchPositionTypes"
                        @input="getDataFromPosition"
                      />
                    </v-card>
                  </v-menu>
                </div>
              </v-col>
            </template>
            <template v-else>
              <v-col cols="12" md="12" class="my-0 py-0">
                <h1 class="mt-3 mb-1">
                  {{ $t(`views.watchList.watchModal.watchForm.editingTitle`) }}
                </h1>
              </v-col>
            </template>
          </v-row>

          <WatchForm :position.sync="watch" :loading="loading" :editing="editing" />
        </v-card-text>

        <ModalButtons
          :editing="editing"
          :loading="loading"
          :saveText="$t('common.save')"
          :saveDisabled="saveDisabled"
          @delete="confirmDelete"
          @cancel="closeModal"
          @save="saveWatch"
        />
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import { mapState } from 'vuex'
import { pick, get } from 'lodash'
import { createWatch, editWatch, deleteWatch } from '@/lib/api.js'
import { getPositionTypes } from '@/lib/positions/positions.js'
import modalMixin from '@/views/components/core/modalMixin.js'
import PositionModalMixin from '@/views/components/common/positions/PositionModalMixin'
import ApiErrorAlert from '@/views/components/common/ApiErrorAlert'
import WatchForm from '@/views/components/watchList/WatchForm'
import PositionSearch from '@/views/components/common/PositionSearch'
import gtag from '@/lib/gtag'

export default {
  components: {
    ApiErrorAlert,
    WatchForm,
    PositionSearch,
  },
  mixins: [modalMixin, PositionModalMixin],
  props: {
    editing: {
      type: Boolean,
      default: false,
    },
    editItem: {
      type: Object,
      default: () => ({}),
    },
  },
  data: () => ({
    watch: null,
    loading: false,
    error: null,
    getDataFromPositionActive: false,
  }),
  computed: {
    ...mapState(['user']),
    saveDisabled() {
      return this.positionNotCompleted(this.watch)
    },
    watchPositionTypes() {
      return getPositionTypes({ model: 'watch' })
    },
  },
  watch: {
    editItem: {
      handler() {
        if (this.editing) {
          this.watch = JSON.parse(JSON.stringify(this.editItem))
        } else {
          this.resetForm()
        }
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    resetForm() {
      this.$set(this, 'watch', {
        type: 'stock',
        targets: { bidPrice: { notifications: [] }, askPrice: { notifications: [] } },
      })
      this.error = null
      this.loading = false
      this.getDataFromPositionActive = false
    },
    async confirmDelete() {
      if (
        await this.$root.$confirm({
          title: this.$t('views.watchList.watchModal.confirm.title'),
          message: this.$t('views.watchList.watchModal.confirm.message'),
        })
      ) {
        await this.deleteWatch()
      }
    },
    getDataFromPosition(item) {
      this.getDataFromPositionActive = false
      if (item) {
        this.$set(this.watch, 'type', get(item, 'type', undefined))
        this.$set(this.watch, 'ticker', get(item, 'ticker', undefined))

        // time out to skip watchers (same in PositionForm)

        setTimeout(() => {
          this.$set(this.watch, 'country', get(item, 'country', undefined))
        }, 100)

        setTimeout(() => {
          this.$set(this.watch, 'exchange', get(item, 'exchange', undefined))
          this.$set(this.watch, 'currency', get(item, 'currency', undefined))
          this.$set(this.watch, 'name', get(item, 'name', undefined))
          this.$set(this.watch, 'sector', get(item, 'sector', undefined))
          this.$set(this.watch, 'manager', get(item, 'manager', undefined))
          this.$set(this.watch, 'tags', get(item, 'tags', undefined))
        }, 300)
      }
    },
    async saveWatch() {
      this.loading = true
      const attributes = [
        'type',
        'ticker',
        'country',
        'exchange',
        'currency',
        'targets',
        'name',
        'sector',
        'manager',
        'tags',
        'notes',
      ]
      const editAttributes = [
        'ticker',
        'country',
        'exchange',
        'targets',
        'name',
        'sector',
        'manager',
        'tags',
        'notes',
      ]

      try {
        if (!this.watch.targets.bidPrice.price)
          this.$set(this.watch, 'targets.bidPrice.price', null)
        if (!this.watch.targets.askPrice.price)
          this.$set(this.watch, 'targets.askPrice.price', null)

        if (['fund'].indexOf(this.watch.type) > -1) {
          delete this.watch.exchange
        }
        if (['fund'].indexOf(this.watch.type) > -1) {
          delete this.watch.country
        }

        if (this.editing) {
          const watch = pick(this.watch, editAttributes)
          await editWatch(this.watch._id, watch)
        } else {
          const watch = pick(this.watch, attributes)
          await createWatch(watch)
        }

        this.$root.$snackbar.success({ text: this.$t('common.saveOk') })
        gtag.pageview({
          pagePath: `/watchlist/${this.editing ? 'edit' : 'create'}/success`,
          pageTitle: `watchlist|${this.editing ? 'edit' : 'create'}|success`,
          pageContentGroup: 'modal',
        })

        this.$emit('saved')
        this.closeModal()
      } catch (error) {
        this.error = get(error, 'data.name', null)
        if (!this.error) {
          this.$root.$snackbar.error({ text: this.$t('common.saveError') })
        } else {
          this.$refs.apiErrorAlert.scrollToError()
        }
        gtag.pageview({
          pagePath: `/watchlist/${this.editing ? 'edit' : 'create'}/error`,
          pageTitle: `watchlist|${this.editing ? 'edit' : 'create'}|error`,
          pageContentGroup: 'modal',
        })
      }
      this.loading = false
    },
    async deleteWatch() {
      this.loading = true
      try {
        await deleteWatch(this.watch)
        this.$emit('saved')
        this.closeModal()

        this.$root.$snackbar.success({ text: this.$t('common.deleteOk') })
        gtag.pageview({
          pagePath: `/watchlist/delete/success`,
          pageTitle: `watchlist|delete|success`,
          pageContentGroup: 'modal',
        })
      } catch (error) {
        this.$root.$snackbar.error({ text: this.$t('common.deleteError') })
        gtag.pageview({
          pagePath: `/watchlist/delete/error`,
          pageTitle: `watchlist|delete|error`,
          pageContentGroup: 'modal',
        })
      }
      this.loading = false
    },
  },
}
</script>

<style lang="scss">
#watch-modal-card {
  h1 {
    font-size: 16px;
  }
  .watch-form-select-data-from-position a {
    font-size: 14px;
  }
}
</style>
